import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard, 
  FileText, 
  AlertTriangle, 
  History,
  LogOut,
  CheckCircle,
  Eye,
  Loader2,
  Users,
  Building,
  MapPin,
  Store,
  Gift,
  BarChart3,
  XCircle,
  Image as ImageIcon,
  Wallet,
  DollarSign,
  Package
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = '/api';

export default function AdminOCRExceptions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [user, setUser] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [selectedExc, setSelectedExc] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  
  // Persist site filter
  const [siteFilter, setSiteFilter] = useState(searchParams.get("site") || "all");

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchExceptions();
      updateUrlParams();
    }
  }, [siteFilter, user]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      fetchSites();
    } catch (error) {
      navigate("/admin/login");
    }
  };

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/sites`, { withCredentials: true });
      setSites(response.data);
    } catch (error) {
      console.error("Failed to fetch sites:", error);
    }
  };

  const updateUrlParams = () => {
    const params = new URLSearchParams();
    if (siteFilter !== "all") params.set("site", siteFilter);
    setSearchParams(params, { replace: true });
  };

  const fetchExceptions = async () => {
    setLoading(true);
    try {
      const siteParam = siteFilter !== "all" ? `?site_id=${siteFilter}` : "";
      const response = await axios.get(`${API}/ocr/exceptions${siteParam}`, { withCredentials: true });
      setExceptions(response.data);
    } catch (error) {
      console.error("Failed to fetch exceptions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (registrationId) => {
    setApproving(registrationId);
    try {
      await axios.post(`${API}/ocr/approve/${registrationId}`, {}, { withCredentials: true });
      setExceptions(prev => prev.filter(e => e.registration_id !== registrationId));
      toast.success("Registration approved");
    } catch (error) {
      toast.error("Failed to approve");
    } finally {
      setApproving(null);
    }
  };

  const handleReject = async (registrationId) => {
    setRejecting(registrationId);
    try {
      await axios.post(`${API}/ocr/reject/${registrationId}`, {}, { withCredentials: true });
      setExceptions(prev => prev.filter(e => e.registration_id !== registrationId));
      toast.success("Registration rejected");
    } catch (error) {
      toast.error("Failed to reject");
    } finally {
      setRejecting(null);
    }
  };

  const openImageModal = (exc) => {
    setSelectedExc(exc);
    setShowImageModal(true);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {}
    navigate("/admin/login");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="admin-container min-h-screen" data-testid="admin-ocr-exceptions">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ParkWise
          </span>
        </div>

        <nav className="space-y-2">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>
          <Link to="/admin/registrations" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <FileText className="w-5 h-5" />
            <span>Registrations</span>
          </Link>
          <Link to="/admin/sites" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Building className="w-5 h-5" />
            <span>Sites & Kiosks</span>
          </Link>
          <Link to="/admin/retailers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Store className="w-5 h-5" />
            <span>Retailers</span>
          </Link>
          <Link to="/admin/rewards" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Gift className="w-5 h-5" />
            <span>Rewards</span>
          </Link>
          <Link to="/admin/analytics" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <BarChart3 className="w-5 h-5" />
            <span>Analytics</span>
          </Link>
          <Link to="/admin/ocr-exceptions" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white">
            <AlertTriangle className="w-5 h-5" />
            <span>OCR Exceptions</span>
          </Link>
          <Link to="/admin/audit-log" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <History className="w-5 h-5" />
            <span>Audit Log</span>
          </Link>
          
          {/* Commercial Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Commercial</p>
            <Link to="/admin/commercial" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Wallet className="w-5 h-5" /><span>Commercial Dashboard</span>
            </Link>
            <Link to="/admin/commercial/finance" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <DollarSign className="w-5 h-5" /><span>Finance Centre</span>
            </Link>
            <Link to="/admin/commercial/products-tariffs" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Package className="w-5 h-5" /><span>Products & Tariffs</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full">
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              OCR Exceptions
            </h1>
            <p className="text-slate-400">Blue Badge scans requiring manual verification</p>
          </div>
          
          {/* Site Filter */}
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="w-[220px] bg-slate-900 border-slate-700 text-white" data-testid="site-filter">
              <MapPin className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by site" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all" className="text-white">All Sites</SelectItem>
              {sites.map(site => (
                <SelectItem key={site.site_id} value={site.site_id} className="text-white">{site.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="admin-card border-slate-800 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : exceptions.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-slate-400">
                {siteFilter !== "all" ? "No exceptions at this site" : "No exceptions pending review"}
              </p>
            </div>
          ) : (
            <Table className="admin-table">
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400">Vehicle</TableHead>
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Site</TableHead>
                  <TableHead className="text-slate-400">Reason</TableHead>
                  <TableHead className="text-slate-400">Badge Data</TableHead>
                  <TableHead className="text-slate-400">Images</TableHead>
                  <TableHead className="text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exceptions.map((exc) => (
                  <TableRow key={exc.registration_id} className="border-slate-800/50 hover:bg-slate-800/30">
                    <TableCell className="font-mono text-white">{exc.vehicle_registration || exc.registration_id}</TableCell>
                    <TableCell className="text-slate-300">{exc.full_name || exc.badge_data?.holder_name || "—"}</TableCell>
                    <TableCell className="text-slate-300 text-sm">{exc.site_name || exc.site_id || "—"}</TableCell>
                    <TableCell className="text-slate-300 max-w-xs truncate">{exc.reason}</TableCell>
                    <TableCell className="text-slate-300">
                      {exc.badge_data ? (
                        <div className="text-xs space-y-0.5">
                          <p><span className="text-slate-500">Badge:</span> {exc.badge_data.badge_number || "N/A"}</p>
                          <p><span className="text-slate-500">Holder:</span> {exc.badge_data.holder_name || "N/A"}</p>
                          <p><span className="text-slate-500">Expiry:</span> {exc.badge_data.expiry_date || "N/A"}</p>
                          <p><span className="text-slate-500">Authority:</span> {exc.badge_data.issuing_authority || "N/A"}</p>
                          <p className={exc.badge_data.confidence_score >= 0.8 ? "text-green-400" : "text-amber-400"}>
                            <span className="text-slate-500">Confidence:</span> {exc.badge_data.confidence_score ? `${Math.round(exc.badge_data.confidence_score * 100)}%` : "N/A"}
                          </p>
                        </div>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      {(exc.badge_images?.front || exc.badge_images?.back) ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openImageModal(exc)}
                          className="text-purple-400 hover:text-purple-300 hover:bg-slate-800"
                          data-testid={`view-images-${exc.registration_id}`}
                        >
                          <ImageIcon className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      ) : (
                        <span className="text-slate-500 text-xs">No images</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/registrations/${exc.registration_id}`)}
                          className="text-blue-400 hover:text-blue-300 hover:bg-slate-800"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleApprove(exc.registration_id)}
                          disabled={approving === exc.registration_id}
                          className="text-green-400 hover:text-green-300 hover:bg-slate-800"
                          data-testid={`approve-${exc.registration_id}`}
                        >
                          {approving === exc.registration_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReject(exc.registration_id)}
                          disabled={rejecting === exc.registration_id}
                          className="text-red-400 hover:text-red-300 hover:bg-slate-800"
                          data-testid={`reject-${exc.registration_id}`}
                        >
                          {rejecting === exc.registration_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </>
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      {/* Badge Images Modal */}
      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              Badge Images - {selectedExc?.vehicle_registration}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <p className="text-sm text-slate-400 mb-2">Front of Badge</p>
              {selectedExc?.badge_images?.front ? (
                <img 
                  src={`data:image/jpeg;base64,${selectedExc.badge_images.front}`} 
                  alt="Badge Front" 
                  className="w-full rounded-lg border border-slate-700"
                />
              ) : (
                <div className="w-full h-48 bg-slate-800 rounded-lg flex items-center justify-center">
                  <span className="text-slate-500">No front image</span>
                </div>
              )}
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-2">Back of Badge</p>
              {selectedExc?.badge_images?.back ? (
                <img 
                  src={`data:image/jpeg;base64,${selectedExc.badge_images.back}`} 
                  alt="Badge Back" 
                  className="w-full rounded-lg border border-slate-700"
                />
              ) : (
                <div className="w-full h-48 bg-slate-800 rounded-lg flex items-center justify-center">
                  <span className="text-slate-500">No back image</span>
                </div>
              )}
            </div>
          </div>
          {selectedExc?.badge_data && (
            <div className="mt-4 p-4 bg-slate-800 rounded-lg">
              <h4 className="text-white font-medium mb-2">OCR Extracted Data</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-slate-400">Holder Name:</span>
                  <span className="text-white ml-2">{selectedExc.badge_data.holder_name || "N/A"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Badge Number:</span>
                  <span className="text-white ml-2">{selectedExc.badge_data.badge_number || "N/A"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Expiry Date:</span>
                  <span className="text-white ml-2">{selectedExc.badge_data.expiry_date || "N/A"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Issuing Authority:</span>
                  <span className="text-white ml-2">{selectedExc.badge_data.issuing_authority || "N/A"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Confidence Score:</span>
                  <span className={`ml-2 ${selectedExc.badge_data.confidence_score >= 0.8 ? "text-green-400" : "text-amber-400"}`}>
                    {selectedExc.badge_data.confidence_score ? `${Math.round(selectedExc.badge_data.confidence_score * 100)}%` : "N/A"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
